/**
 * Detalhe do espelho de ponto de um funcionário em um dia.
 * Mostra marcações, horas calculadas conforme CLT, tratamentos e ações.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { espApi, tratApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import { formatHoras, formatCpf } from '../../types/api'
import type { TratamentoTipo } from '../../types/api'

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  ABERTO: { label: 'Aberto', cls: 'badge-yellow' },
  EM_TRATAMENTO: { label: 'Em tratamento', cls: 'badge-blue' },
  FECHADO: { label: 'Fechado', cls: 'badge-gray' },
}

export function EspelhoDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: espelho, isLoading } = useQuery({
    queryKey: ['espelho', id],
    queryFn: () => espApi.get(id!),
    enabled: !!id,
  })

  const { data: tratamentos } = useQuery({
    queryKey: ['tratamentos', id],
    queryFn: () => tratApi.list(id!),
    enabled: !!id,
  })

  const [showTrat, setShowTrat] = useState(false)
  const [showReabrir, setShowReabrir] = useState(false)
  const [tratForm, setTratForm] = useState<{ tipo: TratamentoTipo; motivo: string }>({ tipo: 'ABONO', motivo: '' })
  const [reabrirMotivo, setRabrirMotivo] = useState('')
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const fecharMutation = useMutation({
    mutationFn: () => espApi.fechar(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['espelho', id] }); qc.invalidateQueries({ queryKey: ['espelhos'] }); setActionMsg({ ok: true, text: 'Espelho fechado com sucesso.' }) },
    onError: (err) => setActionMsg({ ok: false, text: getErrorMessage(err) }),
  })

  const reabrirMutation = useMutation({
    mutationFn: () => espApi.reabrir(id!, reabrirMotivo),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['espelho', id] }); setShowReabrir(false); setActionMsg({ ok: true, text: 'Espelho reaberto.' }) },
    onError: (err) => setActionMsg({ ok: false, text: getErrorMessage(err) }),
  })

  const tratMutation = useMutation({
    mutationFn: () => tratApi.create(id!, tratForm.tipo, tratForm.motivo),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tratamentos', id] }); qc.invalidateQueries({ queryKey: ['espelho', id] }); setShowTrat(false); setTratForm({ tipo: 'ABONO', motivo: '' }); setActionMsg({ ok: true, text: 'Tratamento registrado.' }) },
    onError: (err) => setActionMsg({ ok: false, text: getErrorMessage(err) }),
  })

  if (isLoading) return <PageLoading />
  if (!espelho) return <div className="alert alert-danger">Espelho não encontrado.</div>

  const badge = STATUS_BADGE[espelho.status] ?? { label: espelho.status, cls: 'badge-gray' }
  const isClosed = espelho.status === 'FECHADO'

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/rh/espelho')} style={{ fontSize: 'var(--font-size-sm)' }}>← Voltar</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
            Espelho — {new Date(espelho.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </h1>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-1)' }}>
            <span style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
              {formatCpf(espelho.funcionario_cpf_pis)}
            </span>
            <span className={`badge ${badge.cls}`}>{badge.label}</span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>CLT: {espelho.clt_version}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {!isClosed && <button className="btn btn-ghost" onClick={() => setShowTrat(true)}>Registrar tratamento</button>}
          {!isClosed && <button className="btn btn-primary" onClick={() => { if (confirm('Fechar o espelho? Ajustes futuros precisarão de reabertura auditada.')) fecharMutation.mutate() }}>Fechar espelho</button>}
          {isClosed && <button className="btn btn-ghost" onClick={() => setShowReabrir(true)}>Reabrir</button>}
        </div>
      </div>

      {actionMsg && (
        <div className={`alert ${actionMsg.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: 'var(--space-4)' }}>
          {actionMsg.text}
        </div>
      )}

      {/* Horas calculadas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <HoraCard label="Horas normais" value={formatHoras(espelho.horas_normais_s)} color="var(--color-success)" />
        <HoraCard label="Horas extras" value={formatHoras(espelho.horas_extras_s)} color="var(--color-primary)" />
        <HoraCard label="Hora noturna" value={formatHoras(espelho.horas_noturnas_s)} color="var(--color-warning)" />
        <HoraCard label="Falta/Atraso" value={formatHoras(espelho.horas_falta_s)} color="var(--color-danger)" />
      </div>

      {/* Marcações do dia */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Marcações ({espelho.marcacoes?.length ?? 0})
        </h2>
        {!espelho.marcacoes?.length ? (
          <p style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>Nenhuma marcação AFD encontrada para este dia.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {espelho.marcacoes.map((m, i) => (
              <div key={m.nsr} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-3)', backgroundColor: i % 2 === 0 ? 'var(--color-gray-50)' : 'white', borderRadius: 'var(--radius-md)' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', fontFamily: 'monospace', minWidth: 80 }}>NSR {m.nsr}</span>
                <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, minWidth: 70 }}>
                  {new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className={`badge ${m.tipo === 'ENTRADA' ? 'badge-green' : m.tipo === 'SAIDA' ? 'badge-red' : 'badge-gray'}`}>
                  {m.tipo}
                </span>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', marginTop: 'var(--space-4)' }}>
          Tolerância de 5 min aplicada (art. 58 §1º CLT). Hora noturna reduzida (art. 73 CLT). TODO: validar com especialista trabalhista.
        </p>
      </div>

      {/* Histórico de tratamentos */}
      {!!tratamentos?.length && (
        <div className="card">
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
            Histórico de tratamentos
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {tratamentos.map(t => (
              <div key={t.id} style={{ padding: 'var(--space-3)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-1)' }}>
                  <span className={`badge ${t.tipo === 'ABONO' ? 'badge-green' : t.tipo === 'AJUSTE' ? 'badge-blue' : 'badge-gray'}`}>{t.tipo}</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
                    {new Date(t.criado_em).toLocaleString('pt-BR')}
                  </span>
                </div>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-700)' }}>{t.motivo}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal: Novo tratamento */}
      {showTrat && (
        <Modal title="Registrar tratamento" onClose={() => setShowTrat(false)}>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>Tipo</label>
            <select className="input" value={tratForm.tipo} onChange={e => setTratForm(f => ({ ...f, tipo: e.target.value as TratamentoTipo }))}>
              <option value="ABONO">Abono — ausência justificada</option>
              <option value="AJUSTE">Ajuste — correção de horário</option>
              <option value="JUSTIFICATIVA">Justificativa — observação</option>
            </select>
          </div>
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>Motivo (obrigatório)</label>
            <textarea className="input" rows={3} value={tratForm.motivo} onChange={e => setTratForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Descreva o motivo do tratamento…" />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <button className="btn btn-primary" onClick={() => tratMutation.mutate()} disabled={tratMutation.isPending || tratForm.motivo.length < 5}>
              {tratMutation.isPending ? 'Registrando…' : 'Registrar'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowTrat(false)}>Cancelar</button>
          </div>
        </Modal>
      )}

      {/* Modal: Reabrir */}
      {showReabrir && (
        <Modal title="Reabrir espelho" onClose={() => setShowReabrir(false)}>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--space-4)' }}>
            A reabertura é auditada. Informe o motivo.
          </p>
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>Justificativa (mín. 10 caracteres)</label>
            <textarea className="input" rows={3} value={reabrirMotivo} onChange={e => setRabrirMotivo(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <button className="btn btn-primary" onClick={() => reabrirMutation.mutate()} disabled={reabrirMutation.isPending || reabrirMotivo.length < 10}>
              {reabrirMutation.isPending ? 'Reabrindo…' : 'Reabrir'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowReabrir(false)}>Cancelar</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function HoraCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="card" style={{ borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginBottom: 'var(--space-1)' }}>{label}</div>
      <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color }}>{value}</div>
    </div>
  )
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card" style={{ width: '100%', maxWidth: 460 }}>
        <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{title}</h2>
        {children}
      </div>
    </div>
  )
}
